import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import { authApi, getToken, setToken, setUnauthorizedHandler } from "@/lib/apiClient";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setTok] = useState(() => getToken());
  // `loading` covers the initial "do we have a valid token?" check so guards
  // don't bounce to /login before we've confirmed the session.
  const [loading, setLoading] = useState(!!getToken());

  // On a 401 anywhere, drop the session so the guard sends us to /login.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setTok(null);
      setUser(null);
    });
  }, []);

  // Validate an existing token on boot.
  useEffect(() => {
    const t = getToken();
    if (!t) { setLoading(false); return; }
    let cancelled = false;
    authApi.me()
      .then((u) => { if (!cancelled) setUser(u); })
      .catch(() => { if (!cancelled) { setToken(null); setTok(null); setUser(null); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const _apply = useCallback((res) => {
    setToken(res.token);
    setTok(res.token);
    setUser(res.user);
    return res.user;
  }, []);

  const login = useCallback(async ({ email, password }) => {
    if (!email || !password) throw new Error("Email and password required");
    return _apply(await authApi.login({ email, password }));
  }, [_apply]);

  const register = useCallback(async ({ email, password, name, signup_code }) => {
    if (!email || !password) throw new Error("Email and password required");
    return _apply(await authApi.register({ email, password, name, signup_code }));
  }, [_apply]);

  const logout = useCallback(() => {
    setToken(null);
    setTok(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, token, loading, login, register, logout, isAuthenticated: !!token }),
    [user, token, loading, login, register, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
